import React from 'react';
import { makeStyles } from '@material-ui/core';
import AuthPicture from 'assets/img/auth_picture.svg';
import Delete from '@material-ui/icons/DeleteOutlined';
import classNames from 'classnames';
import styles from 'assets/jss/material-dashboard-pro-react/views/registerPageStyle';
import Wrapper from 'components/Wrapper/Wrapper';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import Logo from 'components/Logo/Logo';
import { translate } from 'components/Translation';
import TabsWithDots from 'components/TabsWithDots/TabsWithDots';
import VerticalAlignBottom from '@material-ui/icons/VerticalAlignBottom';
import Button from 'components/CustomButtons/Button';
import CustomButton from 'components/CustomButtons/Button';
import CustomInput from 'components/CustomInput/CustomInput';
import { warningColor } from 'assets/jss/material-dashboard-pro-react';
import CustomLink from 'components/CustomLink/CustomLink';
import FileUpload from 'components/CustomUpload/FileUpload';
import Circle from '@material-ui/icons/CheckCircleOutlined';
import { UsersService } from 'services/users/users';
import { finalize } from 'rxjs/operators';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { Link } from 'react-router-dom';
import { setLanguage } from 'redux/modules/settings/language/actions';
import RTL from 'components/RTL/RTL';
import emailNotValid from 'utils/objectsValidation/emailNotValid';
import numberNotValid from 'utils/objectsValidation/numberNotValid';
import _ from 'lodash';
import { BodyM, BodyS, Caption, CaptionBold, DisplayM, HeadingMBold, HeadingS } from 'components/Typography/Typography';
import BusinessLink from 'components/BusinessLink/BusinessLink';
import CustomTooltip from 'components/CustomTooltip/CustomTooltip';
import { RegisterBusinessAccountData } from 'utils/api/payloads/omantelApi';
import usernameNotValid from 'utils/objectsValidation/usernameNotValid';
import apiConfig from 'utils/api/config';
import { fetchLabels } from 'utils/labels/labels';

const AUTH_LETTER_URL = apiConfig.links.authLetterUrl;

const emptyValues = (list: Array<string>, value: any): FormData =>
	list.reduce((acc, key) => ({ ...acc, [key]: _.cloneDeep(value) }), {}) as FormData;
const getValue = (event: React.FormEvent<HTMLInputElement>) => (event.target as HTMLInputElement).value;

interface FormData {
	companyName: string;
	authorizedPersonName: string;
	username: string;
	documentId: string;
	mobileNumber: string;
	email: string;
}

type FileName = 'authPersonIdFile' | 'idCopy1File' | 'idCopy2File' | 'crLicFile' | 'municipalityLicFile';

const fileNames: Array<{ name: FileName; fileMaxSize: number }> = [
	{ name: 'authPersonIdFile', fileMaxSize: 1024 * 1024 },
	{ name: 'idCopy1File', fileMaxSize: 1024 * 1024 },
	{ name: 'idCopy2File', fileMaxSize: 1024 * 1024 },
	{ name: 'crLicFile', fileMaxSize: 2.5 * 1024 * 1024 },
	{ name: 'municipalityLicFile', fileMaxSize: 2.5 * 1024 * 1024 },
];

interface Files {
	authPersonIdFile: string;
	authPersonIdFileName: string;
	idCopy1File: string;
	idCopy1FileName: string;
	idCopy2File: string;
	idCopy2FileName: string;
	crLicFile: string;
	crLicFileName: string;
	municipalityLicFile: string;
	municipalityLicFileName: string;
}

const formKeys: Array<keyof FormData> = [
	'companyName',
	'authorizedPersonName',
	'username',
	'documentId',
	'mobileNumber',
	'email',
];

enum STEPS {
	FORM = 0,
	AUTH_LETTER,
	UPLOAD,
	THANK_YOU,
}

const RegisterPage = () => {
	const newTheme = { palette: { primary: { main: warningColor[0] } } };

	const language = useSelector((state: any) => state.settings.language);

	// @ts-ignore
	const useStyles = makeStyles(styles);
	const classes = useStyles({ image: AuthPicture });

	const [activeTab, setActiveTab] = React.useState<STEPS>(STEPS.FORM);
	const [form, setForm] = React.useState<FormData>({} as FormData);

	const [files, setFiles] = React.useState(() =>
		emptyValues(
			fileNames.map(({ name }) => name),
			null
		)
	);
	const [showFormErrors, setShowFormErrors] = React.useState(false);
	const [loading, setLoading] = React.useState({ register: false });

	const dispatch = useDispatch();
	const showSummary = () => setActiveTab(STEPS.THANK_YOU);
	const reset = () => {
		setForm(emptyValues(formKeys, ''));
		setFiles(
			emptyValues(
				fileNames.map(({ name }) => name),
				null
			)
		);
	};

	React.useEffect(() => {
		reset();
	}, []);

	const validateForm = (key: keyof FormData) => {
		if (key === 'username') {
			return !usernameNotValid(form[key]);
		} else if (key === 'mobileNumber') {
			return !numberNotValid(form[key]);
		} else if (key === 'email') {
			return !emailNotValid(form[key]);
		} else {
			return form[key].trim().length > 0;
		}
	};

	const handleRegister = () => {
		setLoading((loading) => ({ ...loading, register: true }));
		UsersService.sendRegisterBusinessAccount({
			language,
			documentType: 'COMPANY_REGISTRATION_NUMBER',
			...form,
			...(Object.keys(files).reduce(
				(acc, key) => ({
					...acc,
					[key]: files[key].content,
					[`${key}Name`]: files[key].name,
				}),
				{}
			) as Files),
		} as RegisterBusinessAccountData)
			.pipe(finalize(() => setLoading((loading) => ({ ...loading, register: false }))))
			.subscribe(() => showSummary());
	};

	const nextFn = () => {
		switch (activeTab) {
			case STEPS.FORM:
				setShowFormErrors(true);
				if (formKeys.every((key: keyof FormData) => validateForm(key))) {
					setActiveTab(STEPS.AUTH_LETTER);
				}
				break;

			case STEPS.AUTH_LETTER:
				setActiveTab(STEPS.UPLOAD);
				break;

			case STEPS.UPLOAD:
			default:
				handleRegister();
				break;
		}
	};

	React.useEffect(() => {
		if (Object.values(loading).some(Boolean)) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [loading, dispatch]);

	const setFormData = (name: keyof FormData, value: string) => setForm((form) => ({ ...form, [name]: value }));

	const inputProps = {
		formControlProps: {
			fullWidth: true,
		},
	};

	const isNextEnabled = React.useMemo(() => {
		switch (activeTab) {
			case STEPS.FORM:
				return Object.keys(form).every((key) => form[key].trim().length > 0);

			case STEPS.AUTH_LETTER:
				return true;

			case STEPS.UPLOAD:
				return Object.values(files).every(Boolean);

			default:
				return false;
		}
	}, [activeTab, form, files]);

	const tabs = [STEPS.AUTH_LETTER, STEPS.FORM, STEPS.UPLOAD].includes(activeTab) && (
		<>
			<BusinessLink>
				<Logo size="rg" color="color" />
			</BusinessLink>
			<div className={classes.welcome}>
				<BodyM disableMargin>{translate('login.welcome')}</BodyM>
			</div>
			<DisplayM color="brand">{translate('login.enterprise')}</DisplayM>
			<div className={classes.signUp}>
				<HeadingS color="primary-light">{translate('register.signup')}</HeadingS>
			</div>
			<div className={classes.tabsWrapper}>
				<TabsWithDots
					tabs={[
						{
							name: translate('info'),
							content: (
								<div className={classes.info}>
									<CustomInput
										{...inputProps}
										id="companyName-input"
										labelText={translate('company.name')}
										inputProps={{
											value: form.companyName,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData(
													'companyName',
													getValue(event).replace(/[^a-zA-Z0-9\u0600-\u06FF ]+/g, '')
												),
										}}
									/>
									<CustomInput
										{...inputProps}
										labelText={translate('register.authorizedPersonName')}
										id="authorisedPerson-input"
										inputProps={{
											value: form.authorizedPersonName,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData(
													'authorizedPersonName',
													getValue(event).replace(/[^a-zA-Z\u0600-\u06FF ]+/g, '')
												),
										}}
									/>
									<CustomInput
										{...inputProps}
										labelText={translate('register.username')}
										id="username-input"
										error={showFormErrors && !validateForm('username')}
										helperText={
											showFormErrors && !validateForm('username')
												? translate('error.invalid.username.format')
												: ''
										}
										inputProps={{
											maxLength: 16,
											value: form.username,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData('username', getValue(event).replace(/[^a-zA-Z0-9]+/g, '')),
										}}
									/>
									<CustomInput
										{...inputProps}
										labelText={translate('register.crNumber')}
										id="documentId-input"
										inputProps={{
											value: form.documentId,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData('documentId', getValue(event).replace(/[^0-9 +]+/, '')),
										}}
									/>
									<CustomInput
										{...inputProps}
										labelText={translate('register.mobileNumber')}
										id="mobileNumber-input"
										error={showFormErrors && !validateForm('mobileNumber')}
										helperText={
											showFormErrors && !validateForm('mobileNumber')
												? translate('error.invalid.mobileNumber.format')
												: ''
										}
										inputProps={{
											value: form.mobileNumber,
											maxLength: 8,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData('mobileNumber', getValue(event).replace(/[^0-9]$/, '')),
										}}
									/>
									<CustomInput
										{...inputProps}
										error={showFormErrors && !validateForm('email')}
										id="email-input"
										helperText={
											showFormErrors && !validateForm('email')
												? translate('error.invalid.email.format')
												: ''
										}
										labelText={translate('register.email')}
										inputProps={{
											value: form.email,
											onChange: (event: React.FormEvent<HTMLInputElement>) =>
												setFormData(
													'email',
													getValue(event).replace(
														/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/g,
														''
													)
												),
										}}
									/>
								</div>
							),
						},
						{
							name: translate('download'),
							content: (
								<div className={classes.center}>
									<HeadingMBold>{translate('register.pleasePrint')}</HeadingMBold>
									<CustomLink
										to={AUTH_LETTER_URL}
										color="brand"
										linkProps={{
											onClick: (event: React.FormEvent<HTMLInputElement>) => {
												event.preventDefault();
												window.open(AUTH_LETTER_URL);
											},
										}}
									>
										<div className={classes.download}>
											<VerticalAlignBottom />
											{translate('register.downloadLetter')}
										</div>
									</CustomLink>
								</div>
							),
						},
						{
							name: translate('upload'),
							content: (
								<div>
									<div className={classes.center}>
										<div className={classes.pleaseUpload}>
											<HeadingS>{translate('register.pleaseUpload')}</HeadingS>
										</div>
									</div>
									{fileNames.map(({ name: key, fileMaxSize }) => (
										<div className={classNames(classes.uploadContainer, `${key}-upload`)} key={key}>
											{files[key] ? (
												<div className={classNames(classes.upload, classes.file)}>
													<Caption color="disabled-light">
														{translate(`register.files.${key}`)}
													</Caption>
													<CustomButton
														color="tertiary-light"
														className={classes.deleteIcon}
														justIcon
														size="lg"
														onClick={() => setFiles((files) => ({ ...files, [key]: null }))}
													>
														<Delete />
													</CustomButton>
													<div className={classes.fileNameContainer}>
														<Circle className={classes.success} />
														<CaptionBold inline className={classes.filename}>
															{files[key].name}
														</CaptionBold>
													</div>
												</div>
											) : (
												<div className={classes.uploadItem}>
													<FileUpload
														iconPosition="right"
														size="sm"
														fileMaxSize={fileMaxSize}
														className={classes.upload}
														text={translate(`register.files.${key}`)}
														onFileChange={(file) =>
															setFiles((files) => ({
																...files,
																[key]: { ...files[key], content: file },
															}))
														}
														onMetadataChange={(data) =>
															setFiles((files) => ({
																...files,
																[key]: { ...files[key], ...data },
															}))
														}
													/>
													<CustomTooltip>
														{translate(`register.files.${key}Info`)}
													</CustomTooltip>
												</div>
											)}
										</div>
									))}
								</div>
							),
						},
					]}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
				/>
				<div className={classes.tabsButtons}>
					{activeTab !== STEPS.FORM && (
						<div className={classNames(classes.fullWidth, classes.right)}>
							<Button color="tertiary-light" onClick={() => setActiveTab((activeTab) => activeTab - 1)}>
								{translate('back')}
							</Button>
						</div>
					)}
					<div className={classNames(classes.fullWidth, { [classes.nextButton]: activeTab !== STEPS.FORM })}>
						<Button
							color="primary-light"
							disabled={!isNextEnabled || Object.values(loading).some(Boolean)}
							className={classes.fullWidth}
							onClick={nextFn}
						>
							{translate(activeTab === STEPS.UPLOAD ? 'send' : 'next')}
						</Button>
					</div>
				</div>
				<div className={classes.center}>
					<BodyM color="primary-light" inline>
						<>{translate('register.alreadyHaveAccount')} </>
					</BodyM>
					<CustomLink to="/auth/login" color="brand" type="bodyMBold">
						{translate('register.login')}
					</CustomLink>
				</div>
			</div>
		</>
	);

	const summary = STEPS.THANK_YOU === activeTab && (
		<div className={classNames(classes.center, classes.fullWidth)}>
			<Circle className={classNames(classes.successIcon, classes.success)} />
			<DisplayM>{translate('thank.you')}</DisplayM>
			<BodyM className={classes.formSuccessfullySent}>{translate('register.successfullySubmitted')}</BodyM>
			<BodyS className={classes.request}>{translate('register.confirmationLetterSent')}</BodyS>
			<Link to="/auth/login" className={classes.summaryLink}>
				{translate('register.backTo')}
			</Link>
			<Logo size="rg" color="color" className={classes.logo} />
		</div>
	);
	const getLanguage = language === 'en' ? 'ar' : 'en';
	return (
		<RTL theme={newTheme}>
			<GridContainer justify="flex-end" className={classes.grid}>
				<GridItem sm={10} md={8} lg={6} xs={12}>
					<Card className={classes.page}>
						<>
							<Button
								color="tertiary-dark"
								className={classes.langButton}
								onClick={() => {
									fetchLabels(getLanguage);
									dispatch(setLanguage(getLanguage));
								}}
							>
								{translate(`auth.${language === 'ar' ? 'en' : 'ar'}`)}
							</Button>
							{tabs}
							{summary}
						</>
					</Card>
					<p className={classNames(classes.copyright)}>{translate('copyright')}</p>
				</GridItem>
			</GridContainer>
		</RTL>
	);
};

export default Wrapper(RegisterPage);
